/** @file Help / Anleitung view. */
import { h, clear } from "./dom.js";

/** @param {string} text @param {string} [cls] */
const p = (text, cls) => h("p", cls ? { class: cls } : null, [text]);

/** @param {string} text */
const h2 = (text) => h("h2", { class: "text-xl font-bold mt-8 mb-3 text-neutral-100" }, [text]);

/** @param {string} text */
const h3 = (text) => h("h3", { class: "text-lg font-semibold mt-5 mb-2 text-neutral-200" }, [text]);

/** @param {string[][]} rows @param {string[]} headers */
function table(headers, rows) {
  const thead = h("thead", null, [
    h("tr", null, headers.map((hd) => h("th", { class: "text-left px-3 py-2 text-neutral-300 border-b border-neutral-700" }, [hd]))),
  ]);
  const tbody = h("tbody", null, rows.map((row) =>
    h("tr", null, row.map((cell) => h("td", { class: "px-3 py-2 border-b border-neutral-800" }, [cell]))),
  ));
  return h("table", { class: "w-full text-sm mb-4" }, [thead, tbody]);
}

/** @param {string[]} items */
const ol = (items) => h("ol", { class: "list-decimal list-inside space-y-1 mb-4 text-neutral-300" }, items.map((t) => h("li", null, [t])));

/** @param {string[]} items */
const ul = (items) => h("ul", { class: "list-disc list-inside space-y-1 mb-4 text-neutral-300" }, items.map((t) => h("li", null, [t])));

/**
 * @param {HTMLElement} host
 */
export function mountHelpView(host) {
  clear(host);

  const content = h("div", { class: "max-w-2xl mx-auto py-8 px-4 text-neutral-300 leading-relaxed" }, [
    h("h1", { class: "text-3xl font-bold mb-2 text-neutral-100" }, ["Anleitung"]),
    p("Handballplan ist ein webbasiertes Taktikboard für Handball-Trainer."),

    // 1. Registrieren & Anmelden
    h2("1. Registrieren & Anmelden"),
    p("Beim ersten Besuch wirst du automatisch zur Anmeldung weitergeleitet."),
    h3("Neues Konto erstellen"),
    ol([
      "Klicke auf \"Registrieren\" unter dem Login-Formular",
      "Gib deine E-Mail-Adresse und ein Passwort (min. 6 Zeichen) ein",
      "Klicke auf \"Registrieren\"",
    ]),
    h3("Anmelden"),
    ul([
      "E-Mail + Passwort: Gib deine Zugangsdaten ein und klicke auf \"Einloggen\"",
      "Google-Konto: Klicke auf \"Mit Google anmelden\"",
    ]),
    h3("Passwort vergessen?"),
    p("Klicke auf \"Passwort vergessen\" auf der Login-Seite. Du erhältst eine E-Mail mit einem Link zum Zurücksetzen."),

    // 2. Editor
    h2("2. Der Editor"),
    p("Nach dem Login landest du im Editor mit einem Handball-Spielfeld (40 x 20 m)."),
    ul([
      "Blaue Tokens = Heimmannschaft (Spieler 1\u20138 + Torwart)",
      "Rote Tokens = Gastmannschaft (Spieler 1\u20138 + Torwart)",
      "Schwarz-weißer Token = Ball",
      "Jeder Spieler zeigt Nummer und Position (TW, LA, RL, RM, RR, RA, KM)",
    ]),
    h3("Spieler bewegen"),
    p("Ziehe einen Token mit der Maus oder dem Finger an die gewünschte Position."),
    h3("Spieler bearbeiten"),
    p("Tippe auf einen Token, um Nummer, Position und Team zu ändern."),

    // 3. Werkzeugleiste
    h2("3. Werkzeugleiste"),
    table(
      ["Symbol", "Werkzeug", "Funktion"],
      [
        ["\u2196", "Auswählen", "Spieler verschieben, Pfeile auswählen"],
        ["\u2192", "Laufweg", "Laufwege als Pfeile zeichnen"],
        ["\u21E2", "Pass", "Passwege zeichnen (gestrichelt)"],
        ["\u26A1", "Schuss", "Schusswege zeichnen"],
        ["\u2715", "Löschen", "Einzelne Pfeile durch Antippen entfernen"],
      ],
    ),
    h3("Farben"),
    p("Rechts neben den Werkzeugen findest du 6 Farben. Klicke auf eine Farbe, bevor du einen Pfeil zeichnest."),
    h3("Rückgängig / Wiederherstellen"),
    ul([
      "\u21BA Rückgängig: Letzte Aktion rückgängig (Ctrl+Z)",
      "\u21BB Wiederherstellen: Wiederherstellen (Ctrl+Shift+Z)",
    ]),

    // 4. Speichern
    h2("4. Spielzüge speichern"),
    ol([
      "Klicke auf \uD83D\uDCBE Speichern (oder Ctrl+S)",
      "Beim ersten Speichern: Spielzug-Name eingeben",
      "Optional: Team zuweisen",
      "Klicke auf \"Speichern\"",
    ]),
    p("Bei weiteren Änderungen wird automatisch unter demselben Namen gespeichert."),

    // 5. Scenes
    h2("5. Szenen & Animation"),
    p("Ein Spielzug kann aus mehreren Szenen bestehen, die als Animation abgespielt werden."),
    h3("Szenen verwalten"),
    ul([
      "Die Timeline am unteren Rand zeigt alle Szenen als Vorschaubilder",
      "Klicke auf \"+\" für eine neue Szene",
      "Jede Szene hat eine einstellbare Dauer (Standard: 2000 ms)",
      "Verschiebe die Spieler in jeder Szene an ihre neue Position",
    ]),
    h3("Animation abspielen"),
    table(
      ["Symbol", "Funktion"],
      [
        ["\u23EE", "Zurück zur ersten Szene"],
        ["\u23EA", "Eine Szene zurück"],
        ["\u25B6", "Animation abspielen / pausieren"],
        ["\u23E9", "Eine Szene vor"],
      ],
    ),
    p("Tipp: Erstelle 3\u20135 Szenen für einen flüssigen Spielzug. Die Spieler werden automatisch zwischen den Szenen animiert.", "text-blue-400 text-sm mb-4"),

    // 6. Teilen
    h2("6. Spielzüge teilen"),
    p("Teile Spielzüge per Link mit deinem Team \u2014 kein Konto nötig zum Anschauen."),
    ol([
      "Öffne einen gespeicherten Spielzug",
      "Klicke auf \uD83D\uDD17 Teilen",
      "Klicke auf \"Link erstellen\"",
      "Kopiere den Link und schicke ihn per WhatsApp, E-Mail oder Messenger",
    ]),

    // 7. Bibliothek
    h2("7. Meine Spielzüge"),
    p("Öffne die Bibliothek über das Benutzer-Menü (rundes Symbol oben rechts) \u2192 Meine Spielzüge."),
    ul([
      "Öffnen \u2014 Spielzug im Editor laden",
      "Teilen \u2014 Share-Link erstellen",
      "Umbenennen \u2014 Namen ändern",
      "Löschen \u2014 Spielzug dauerhaft entfernen",
    ]),

    // 8. Teams
    h2("8. Teams verwalten"),
    p("Unter Benutzer-Menü \u2192 Teams kannst du:"),
    ul([
      "Neue Teams anlegen (Name + Farbe)",
      "Kader mit Spielernamen und Nummern pflegen",
      "Teams bei Spielzügen zuweisen",
    ]),

    // 9. Hallen-Modus
    h2("9. Hallen-Modus"),
    p("Klicke auf \uD83C\uDFDF oben rechts \u2014 das Spielfeld wechselt in den Vollbildmodus mit größeren Touch-Flächen für das Tablet."),
    p("Tipp: Tablet im Querformat halten für beste Ergebnisse.", "text-blue-400 text-sm mb-4"),

    // 10. Konto
    h2("10. Konto"),
    p("Unter Benutzer-Menü \u2192 Konto findest du:"),
    ul([
      "Passwort-Reset-Mail senden",
      "Datenexport (DSGVO Art. 20) \u2014 alle Daten als JSON",
      "Konto löschen \u2014 unwiderruflich",
    ]),

    // 11. Tastenkürzel
    h2("11. Tastenkürzel"),
    table(
      ["Kürzel", "Funktion"],
      [
        ["Ctrl+S", "Spielzug speichern"],
        ["Ctrl+Z", "Rückgängig"],
        ["Ctrl+Shift+Z", "Wiederherstellen"],
      ],
    ),

    // 12. Tipps
    h2("12. Tipps für Trainer"),
    ul([
      "In der Halle: Tablet im Querformat, Hallen-Modus aktivieren",
      "Vorbereitung: Spielzüge am PC erstellen, dann in der Halle abrufen",
      "Team-Briefing: Link per WhatsApp-Gruppe teilen",
      "Varianten: Gleichen Spielzug unter verschiedenen Namen speichern",
    ]),

    // Kontakt
    h("div", { class: "mt-10 pt-6 border-t border-neutral-700 text-sm text-neutral-400" }, [
      p("Bei Fragen oder Problemen: info@handballplan.de"),
    ]),
  ]);
  host.appendChild(content);
}
